import Routes from 'routes/index'
import React, { Suspense, useEffect } from 'react'
import Loader from 'components/ui-kit/Loader/index'
import 'react-toastify/dist/ReactToastify.css'
import { LOADER_HANDLER_TYPES } from 'constants/index'
import { useSelector, useDispatch } from 'react-redux'
import ErrorBoundary from 'components/layout/ErrorBoundary/index'
import { ToastContainer } from 'react-toastify'
import useWalletSocketNameSpace from 'socket-resources/hooks/useWalletSocketNameSpace'
import { updateWalletBalance } from 'redux-store/redux/slices/auth.slice'
import LoaderPage from 'components/molecules/Loader/index'
import { muteAllSounds, unmuteAllSounds } from 'utils/audio.utils'

import * as Sentry from '@sentry/react'
import { AxiosError } from '../node_modules/axios/index'

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  enabled: true,
  sendDefaultPii: true,
  // Configure grouping for better error organization
  beforeSend (event, hint) {
    // Check if this is an axios error
    const isAxiosError = hint.originalException instanceof AxiosError

    if (isAxiosError) {
      const axiosError = hint.originalException
      const requestUrl = axiosError.config.url
      const requestMethod = axiosError.config.method?.toUpperCase()
      const statusCode = axiosError.response?.status

      console.log(`SKIPPING SENTRY FOR AXIOS ERROR: ${requestMethod} ${requestUrl} ${statusCode}`)
      return null // This prevents the event from being sent to Sentry
    }

    console.log('SENDING EVENT TO SENTRY', event)
    return event
  },
  // Configure grouping behavior
  groupingFunc: (stacktrace, options) => {
    // Custom grouping for Axios errors
    if (stacktrace && stacktrace.frames && stacktrace.frames.length > 0) {
      const firstFrame = stacktrace.frames[0]
      if (firstFrame.filename && firstFrame.filename.includes('axios')) {
        // Group by URL pattern for Axios errors
        const urlMatch = firstFrame.function_name?.match(/axios_(\w+)_(.+)_(\d+)/)
        if (urlMatch) {
          return `axios_${urlMatch[1]}_${urlMatch[2]}_${urlMatch[3]}`
        }
      }
    }
    return options.fingerprint || 'default'
  }
})

function App () {
  const dispatch = useDispatch()

  const pageLoading = useSelector(
    (state) => state.loader[LOADER_HANDLER_TYPES.page]
  )

  const { userInfo } = useSelector((state) => state.auth)

  const { listenWalletUpdateEvent } = useWalletSocketNameSpace()

  const handleWalletUpdate = ({ data }) => {
    data?.balance && dispatch(updateWalletBalance(data.balance))
  }

  const toggleSounds = () => {
    document.visibilityState === 'visible'
      ? unmuteAllSounds()
      : muteAllSounds()
  }

  // Initialize audio context on user interaction
  const initializeAudioContext = () => {
    try {
      // This will initialize the audio context when user interacts
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      if (audioContext.state === 'suspended') {
        audioContext.resume()
      }
    } catch (error) {
      console.warn('Audio context initialization failed:', error.message)
    }
  }

  useEffect(() => {
    document.addEventListener('visibilitychange', toggleSounds)

    // Add user interaction listeners to initialize audio context
    const userInteractionEvents = ['click', 'touchstart', 'keydown']
    userInteractionEvents.forEach(event => {
      document.addEventListener(event, initializeAudioContext, { once: true })
    })

    return () => {
      document.removeEventListener('visibilitychange', toggleSounds)
      userInteractionEvents.forEach(event => {
        document.removeEventListener(event, initializeAudioContext)
      })
    }
  }, [])

  useEffect(() => {
    if (userInfo) {
      const cleaner = listenWalletUpdateEvent(handleWalletUpdate)
      return () => cleaner()
    }
  }, [userInfo])

  return (
    <Suspense fallback={<LoaderPage />}>
      <ErrorBoundary>
        <ToastContainer
          position='top-right'
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme='colored'
        />
        {pageLoading && (
          <div className='page-loader'>
            <Loader />
          </div>
        )}
        <Routes />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App
